import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useI18n } from "../i18n";
import { useStudentAuth } from "../StudentAuthContext";

/**
 * Glassmorphism header. Sits above the bento layout, so it does not
 * use mix-blend-difference (background tone is consistent off-white).
 *
 * Slots:
 *   left    — brand wordmark
 *   right   — student account affordance · "back" link · EN/ES toggle
 *
 * The student account affordance (Sign in / My lessons) is omitted on the
 * pages that already render their own version (`/students`) and on the
 * auth screens themselves (`/login`, `/register`) to avoid duplication.
 */
const HIDE_ACCOUNT_ON = new Set([
  "/students",
  "/login",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/teachers/forgot",
]);

export default function Header() {
  const { lang, toggle, t } = useI18n();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isContact = location.pathname === "/contact";
  const showAccount = !HIDE_ACCOUNT_ON.has(location.pathname);
  const { status, student, logout } = useStudentAuth();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-[#1C1C1C]/5">
      <div className="px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-brand-link"
          className="font-display text-lg md:text-xl tracking-tight font-medium text-[#1C1C1C] hover:opacity-70 transition-opacity"
        >
          {t("brand")}
        </Link>

        <div className="flex items-center gap-5 md:gap-7">
          {showAccount && status === "auth" && (
            <div className="flex items-center gap-3 md:gap-4" data-testid="header-account-auth">
              <span className="hidden md:inline text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55">
                Hola{student?.name ? `, ${student.name.split(" ")[0]}` : ""}
              </span>
              <Link
                to="/me"
                data-testid="header-me-link"
                className="px-3 py-1.5 bg-[#1C1C1C] text-[#FAF9F6] rounded-md text-[10px] tracking-[0.24em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
              >
                My lessons →
              </Link>
              <button
                onClick={logout}
                data-testid="header-signout-btn"
                className="text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/50 hover:text-[#1C1C1C]/80 transition-colors"
              >
                Sign out
              </button>
            </div>
          )}

          {showAccount && status === "anon" && (
            <Link
              to="/login"
              state={{ next: "/me" }}
              data-testid="header-signin-link"
              className="text-[11px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C] hover:text-[#7FA6C8] transition-colors"
            >
              Sign in
            </Link>
          )}

          {!isHome && (
            <Link
              to="/"
              data-testid="header-back-link"
              className="text-[11px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C] hover:text-[#7FA6C8] transition-colors"
            >
              ← {t("backHome")}
            </Link>
          )}
          {!isContact && (
            <Link
              to="/contact"
              data-testid="header-contact-link"
              className="hidden sm:inline text-[11px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C] hover:text-[#7FA6C8] transition-colors"
            >
              {t("contact")}
            </Link>
          )}
          <button
            onClick={toggle}
            data-testid="header-lang-toggle"
            className="text-[11px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]"
            aria-label="Toggle language"
          >
            <span className={lang === "en" ? "" : "opacity-40"}>EN</span>
            <span className="mx-1.5 opacity-40">/</span>
            <span className={lang === "es" ? "" : "opacity-40"}>ES</span>
          </button>
        </div>
      </div>
    </header>
  );
}
